import React, { useState, useEffect } from "react";
import { Formik } from "formik";
import * as yup from "yup";
import Dropzone from "react-dropzone";
import Modal from "../../../../components/modal";
import Select from "../../../../components/form/select";
import TextInput from "../../../../components/form/text-input";
import Button from "../../../../components/new-button";
import styles from "./styles.module.scss";
import useTicketStore from "../../../../stores/ticket";
import { fetchTicketOptions, createTicket, fetchTickets } from "../../../../services/ticket";
import { useDbaSearch } from "../../../../hooks/general/useDbaSearch";
import { FileIconColorMapping } from "../../../../pages/create-app/components/application-form/components/file-upload/utils";
import UploadIcon from "../../../../assets/svg/icons/common/upload";
import CloseIconStroke from "../../../../assets/svg/icons/common/CloseIconStroke";

  const schema = yup.object({
    powerapp_id: yup
      .object()
      .nullable()
      .test("has-value", "* DBA is required", (v) => Boolean(v && v.value)),
    owner_group_id: yup.object().nullable().required("* Owner group is required"),
    problem_type_id: yup.object().nullable().required("* Problem type is required"),
    comment: yup.string().required("* Comment is required").max(5000, "* Comment must be 5000 characters or fewer"),
  });

export const CreateTicketModal = ({ open, onClose }) => {
  const [files, setFiles] = useState([]);
  const { options, merchantTicketScopePowerappId, merchantTicketScopeDba } =
    useTicketStore((state) => ({
      options: state.options,
      merchantTicketScopePowerappId: state.merchantTicketScopePowerappId,
      merchantTicketScopeDba: state.merchantTicketScopeDba,
    }));
  const [optionsLoading, setOptionsLoading] = useState(false);
  // debounceMs: 0 — the Select below is async, and form/select already debounces
  // onSearchChange at 250ms. A second delay here would only stack on top of it.
  const { dbaOptions, dbaLoading, searchDba } = useDbaSearch({ debounceMs: 0 });

  const handleFileUpload = (acceptedFiles) => {
    setFiles(prevFiles => [...prevFiles, ...acceptedFiles]);
  };

  const handleFileRemove = (index) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  const [submitError, setSubmitError] = useState(null);

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    try {
      setSubmitError(null);
      // Create FormData instance
      const formData = new FormData();
      
      // Add form values
      formData.append('powerapp_id', values.powerapp_id?.value);
      formData.append('owner_group_id', values.owner_group_id?.value);
      formData.append('problem_type_id', values.problem_type_id?.value);
      formData.append('comment', values.comment || "");

      // Add files if they exist
      if (files.length > 0) {
        files.forEach(file => {
          formData.append('comment_attachment[]', file);
        });
      }

      const response = await createTicket(formData);
      
      if (response.status) {
        // Refetch tickets to update the list
        await fetchTickets();
        onClose();
      } else {
        setSubmitError(response.message || 'Failed to create ticket');
      }
    } catch (error) {
      console.error("Error creating ticket:", error);
      // The shared axios instance rejects with error.response itself (api.js:56),
      // so the payload is at error.data — error.response.data is never set here.
      // Without this, create()'s 403 "You do not have permission to create a
      // ticket" and 404 "This application no longer exists" are both swallowed.
      // A transport failure rejects with a bare string (api.js:62).
      if (error?.data?.errors) {
        // Handle validation errors from backend
        setErrors(error.data.errors);
      } else {
        setSubmitError(
          error?.data?.message ||
          (typeof error === "string" ? error : null) ||
          'Failed to create ticket'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      header="Create a ticket"
      size="md"
    >
      <Formik
        key={merchantTicketScopePowerappId ?? "all"}
        enableReinitialize
        initialValues={{
          powerapp_id: merchantTicketScopePowerappId != null
            ? { value: merchantTicketScopePowerappId, label: merchantTicketScopeDba || "" }
            : null,
          owner_group_id: null,
          problem_type_id: null,
          comment: "",
        }}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formContent}>
              <div className={styles.formGroup}>
                <Select
                  label="DBA"
                  name="powerapp_id"
                  value={values.powerapp_id?.value ? values.powerapp_id : null}
                  onChange={(option) => {
                    handleChange({
                      target: { name: "powerapp_id", value: option }
                    });
                  }}
                  onBlur={handleBlur}
                  error={touched.powerapp_id && errors.powerapp_id}
                  placeholder="Search DBA"
                  options={dbaOptions}
                  loading={dbaLoading}
                  search
                  async
                  onSearchChange={searchDba}
                />
              </div>

              <div className={styles.formGroup}>
                <Select
                  label="Owner group"
                  name="owner_group_id"
                  value={values.owner_group_id}
                  onChange={(option) => {
                    handleChange({
                      target: { name: "owner_group_id", value: option }
                    });
                  }}
                  onBlur={handleBlur}
                  error={touched.owner_group_id && errors.owner_group_id}
                  placeholder="Select group"
                  options={options.owner_groups}
                  loading={optionsLoading}
                  search
                />
              </div>

              <div className={styles.formGroup}>
                <Select
                  label="Problem type"
                  name="problem_type_id"
                  value={values.problem_type_id}
                  onChange={(option) => {
                    handleChange({
                      target: { name: "problem_type_id", value: option }
                    });
                  }}
                  onBlur={handleBlur}
                  error={touched.problem_type_id && errors.problem_type_id}
                  placeholder="Select type"
                  options={options.problem_types.filter((p) => p.linked)}
                  loading={optionsLoading}
                  search
                />
              </div>

              <div className={styles.formGroup} style={{ gap: 0 }}>
                <label>Comment</label>
                <textarea
                  name="comment"
                  value={values.comment}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Add comment"
                  maxLength={5000}
                />
                {touched.comment && errors.comment && (
                  <div className={styles.error}>{errors.comment}</div>
                )}
              </div>

              <div className={styles.fileUpload}>
                <Dropzone
                  onDrop={handleFileUpload}
                  minSize={100}
                  maxSize={10 * 1024 * 1024}
                  accept={{
                    "image/jpeg": [".jpg", ".jpeg"],
                    "image/png": [".png"],
                    "image/gif": [".gif"],
                    "application/pdf": [".pdf"],
                    "application/msword": [".doc"],
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
                    "application/vnd.ms-excel": [".xls"],
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
                  }}
                  noClick={false}
                  noKeyboard={false}
                >
                  {({ getRootProps, getInputProps }) => (
                    <section className={styles.dnd}>
                      <div {...getRootProps()} className={styles.dnd_section}>
                        <input {...getInputProps()} />
                        <div className={styles.inner_content}>
                          <UploadIcon />
                          <div>
                            <p>
                              Upload documents by drag and drop or{" "}
                              <span>click to upload</span>
                            </p>
                            <p>jpeg, jpg, png, gif, pdf, doc, docx, xls, xlsx</p>
                          </div>
                        </div>
                      </div>
                    </section>
                  )}
                </Dropzone>

                {files.length > 0 && (
                  <div className={styles.fileList}>
                    {files.map((file, index) => (
                      <div key={index} className={styles.fileItem}>
                                              <div 
                        className={styles.fileIcon}
                        style={{
                          backgroundColor: FileIconColorMapping[file.name.split('.').pop().toLowerCase()] || '#D99F00'
                        }}
                      >
                        {file.name.split('.').pop().slice(0, 3).toUpperCase()}
                      </div>
                        <div className={styles.fileInfo}>
                          <span className={styles.fileName}>{file.name}</span>
                          <span className={styles.fileSize}>
                            {Math.round(file.size / 1024)}kb
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleFileRemove(index)}
                          className={styles.removeFile}
                        >
                          <CloseIconStroke />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {submitError && (
                <div className={styles.errorMessage}>
                  {submitError}
                </div>
              )}
            </div>

            <div className={styles.formSidebar}>
              <h3>Instructions</h3>
              <ol>
                <li>Please be descriptive when explaining the issue(s) you are facing.</li>
                <li>If you are uploading supporting documentation, please use descriptive filenames as this will speed up the process.</li>
              </ol>
            </div>


            <div className={styles.actions}>
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Save'}
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </Modal>
  );
}; 